import type { Config } from "tailwindcss";
import sharedConfig from "../core/tailwind.config.js";

/*
 * FlyFetch brand (DiscDog Klub ČR) — web frontend.
 * Overrides the shared preset with the Czech-flag palette (red primary, blue
 * secondary), a warm charcoal "ink" ramp, athletic radii and warm-tinted
 * shadows, and the Barlow / JetBrains Mono type system. Scoped to web/ only —
 * admin keeps its own Skeleton theme.
 */
export default {
	content: ["../core/**/*.{html,css,svelte,ts}", "./src/**/*.{html,css,svelte,ts}", "./*.{html,css,svelte,ts}"],
	presets: [sharedConfig],
	theme: {
		extend: {
			fontFamily: {
				// Repoint the existing family keys so every current `font-montserrat`
				// / `font-oswald` usage across templates adopts the brand fonts.
				sans: ["Barlow", "system-ui", "-apple-system", "sans-serif"],
				montserrat: ["Barlow", "system-ui", "sans-serif"],
				oswald: ["Barlow Condensed", "Arial Narrow", "sans-serif"],
				display: ["Barlow Condensed", "Arial Narrow", "sans-serif"],
				mono: ["JetBrains Mono", "ui-monospace", "SFMono-Regular", "monospace"],
			},
			colors: {
				// Primary = Czech-flag red (energy, actions, "live")
				primary: {
					DEFAULT: "#ed1c24",
					hover: "#cc1219",
					50: "#fdecec",
					100: "#fbd0d2",
					200: "#f5999d",
					300: "#f06067",
					400: "#ed3038",
					500: "#ed1c24",
					600: "#cc1219",
					700: "#a20d13",
					800: "#78090e",
				},
				// Secondary = Czech-flag blue (organizer surfaces, progress, trust)
				secondary: {
					DEFAULT: "#3871c1",
					50: "#eaf1fb",
					100: "#cdddf5",
					200: "#9dbcea",
					300: "#6a99dd",
					400: "#4a80cf",
					500: "#3871c1",
					600: "#2c5ca3",
					700: "#234a83",
					800: "#1a375f",
				},
				// Warm charcoal ink ramp — text and dark surfaces
				ink: {
					50: "#f7f6f4",
					100: "#efeeed",
					200: "#e2e0df",
					300: "#cbc8c8",
					400: "#aba7a7",
					500: "#8a8686",
					600: "#6a6767",
					700: "#4f4c4c",
					800: "#3a3838",
					900: "#2b2a2a",
					950: "#201f1f",
				},
				// Re-map the legacy neutral scales onto the warm ink ramp so existing
				// `text-grey-*` / `bg-light-*` usages become warm charcoal automatically.
				grey: {
					200: "#e2e0df",
					300: "#cbc8c8",
					400: "#aba7a7",
					500: "#8a8686",
					600: "#6a6767",
					700: "#4f4c4c",
					800: "#3a3838",
					900: "#2b2a2a",
				},
				light: {
					100: "#f7f6f4",
					200: "#efeeed",
					300: "#e2e0df",
				},
				// Cool neutral page background — pairs with the dark ink/blue hero bands
				// (replaces the warm sand base, which clashed with the dark banners).
				page: "#eceef2",
				// Status + warm accents (from the mark)
				sand: { 50: "#f6f2ea", 100: "#f2eede", 200: "#ece5d3", 300: "#ddd3b3", 500: "#bfb290" },
				grass: { 100: "#e2f1e4", 500: "#2f9e52", 600: "#237f41" },
				amber: { 100: "#fdf0d6", 500: "#e8a020" },
			},
			borderRadius: {
				md: "10px", // controls
				lg: "14px", // cards
				xl: "14px", // legacy card usages (`rounded-xl`) map to card radius
			},
			boxShadow: {
				brand: "0 6px 18px rgba(237, 28, 36, 0.28)", // red glow — reserved (logo/rare)
				action: "0 6px 18px rgba(56, 113, 193, 0.26)", // blue glow — primary buttons
				card: "0 1px 3px rgba(32, 31, 31, 0.08), 0 1px 2px rgba(32, 31, 31, 0.06)",
				"card-hover": "0 6px 16px rgba(32, 31, 31, 0.1), 0 2px 4px rgba(32, 31, 31, 0.06)",
				elevated: "0 12px 28px rgba(32, 31, 31, 0.14), 0 4px 8px rgba(32, 31, 31, 0.06)",
			},
			transitionTimingFunction: {
				spring: "cubic-bezier(0.34, 1.56, 0.64, 1)",
				snappy: "cubic-bezier(0.16, 1, 0.3, 1)",
			},
		},
	},
} satisfies Config;
